import { useQuery } from '@tanstack/react-query';
import { Formik } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';

import { RESPONSES_QUERY_KEYS } from 'components/responses/constants';
import { api } from 'controllers/network/apiClient';
import { useAuth } from 'hooks/useAuth';
import {
  AgentDashboardResponse,
  useAgentDashboard,
} from '../hooks/useDashboard';
import {
  FILTER_KEYS,
  OUTCOMES_OPTIONS,
  SEARCH_DIRECTION,
  SEARCH_DIRECTION_KEYS,
  SENTIMENT_OPTIONS,
  ShopperInsightsControls,
  SORT_BY,
  SORT_BY_KEYS,
  STATUS_OPTIONS,
  SUMMARY_OPTIONS,
} from '../pages/types';
import ConversationIntelligenceControlsForm from './ConversationIntelligenceControlsForm';

interface Props {
  badgeElements?: any;
  onControlsUpdate: (filters: any) => void;
  controls: ShopperInsightsControls;
  isLoading: boolean;
  isMultiShop: boolean;
  isOpen: boolean;
  toggleFunction: any;
  fullClearFunction?: any;
  parentRef?: React.RefObject<HTMLDivElement>;
}

interface DropdownOption {
  label: string;
  value: string;
}

const transformAgentData = (
  agentData: AgentDashboardResponse,
): DropdownOption[] => {
  const transformedData: DropdownOption[] = [];

  Object.keys(agentData.active_agents).forEach((agentId) => {
    const agentInfo = agentData.active_agents[parseInt(agentId)];

    transformedData.push({
      label: agentInfo.name,
      value: agentId,
    });
  });

  return transformedData;
};

const FOCUSABLE_ELEMENTS =
  'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, *[tabindex], *[contenteditable]';

const returnFocusToActivator = (activator: HTMLElement) => {
  if (typeof activator?.focus === 'function') {
    activator.focus();
  }
};

const setFocus = (
  modalRef: React.RefObject<HTMLDivElement>,
  firstElementRef?: React.RefObject<HTMLElement>,
) => {
  if (firstElementRef?.current) {
    firstElementRef.current.focus();
    return;
  }
  if (modalRef.current) {
    modalRef.current.querySelector('input')?.focus();
  }
};

const trapFocus = (
  event: KeyboardEvent,
  modalRef: React.RefObject<HTMLDivElement>,
) => {
  if (!modalRef.current || event.key !== 'Tab') return;

  const focusable = modalRef.current.querySelectorAll(FOCUSABLE_ELEMENTS);
  const firstFocusable = focusable[0];
  const lastFocusable = focusable[focusable.length - 1];

  if (event.shiftKey && document.activeElement === firstFocusable) {
    event.preventDefault();
    (lastFocusable as HTMLElement).focus();
  }

  if (!event.shiftKey && document.activeElement === lastFocusable) {
    event.preventDefault();
    (firstFocusable as HTMLElement).focus();
  }
};

const ConversationIntelligenceControls = ({
  badgeElements,
  fullClearFunction,
  controls,
  onControlsUpdate,
  isLoading,
  isMultiShop,
  isOpen,
  toggleFunction,
  parentRef,
}: Props) => {
  const { getShopId } = useAuth();
  const modalRef = useRef<HTMLDivElement>(null);
  const [activator, setActivator] = useState<Element | null>(null);

  useEffect(() => {
    if (!isOpen) {
      returnFocusToActivator(activator as HTMLElement);
      return;
    }

    setActivator(document.activeElement);
    setFocus(modalRef);

    const eventHandlers = (event: KeyboardEvent) => {
      trapFocus(event, modalRef);
      if (event.key === 'Escape') {
        toggleFunction(false);
      }
    };

    window.addEventListener('keydown', eventHandlers);

    return () => {
      window.removeEventListener('keydown', eventHandlers);
    };
  }, [isOpen]);

  const [shopSearchText, setShopSearchText] = useState('');
  const [agentSearchText, setAgentSearchText] = useState('');
  const { data: shops } = useQuery({
    queryKey: [RESPONSES_QUERY_KEYS.SMS_SALES_SHOPS],
    queryFn: (): Promise<{ shops: DropdownOption[] }> =>
      api.get('/admin/get_all_shops'),
    select: (data) => data.shops,
  });

  const { data: agentData } = useAgentDashboard();

  const agentOptions = useMemo(() => {
    if (!agentData) return [];

    const agentDropdownOptions = transformAgentData(agentData);

    return agentDropdownOptions.filter((agent) =>
      agent.label.includes(agentSearchText),
    );
  }, [agentData, agentSearchText]);

  const shopOptions = useMemo(() => {
    if (!shops) return [];

    const filterShops = (shop: DropdownOption) => {
      const shopName = shop.label?.toLowerCase();
      const shopId = shop.value.toString();
      const searchString = shopSearchText.toLowerCase();
      return (
        shopId.startsWith(searchString) || shopName?.includes(searchString)
      );
    };

    const getShopOption = (shop: DropdownOption) => ({
      label: `${shop.label} (${shop.value})`,
      value: shop.value,
    });

    return shops.filter(filterShops).map(getShopOption).slice(0, 50);
  }, [shops, shopSearchText]);

  const shopsMap: any = {};
  shopOptions.forEach(({ value, label }: { value: string; label: string }) => {
    shopsMap[value] = { value, label };
  });

  const agentsMap: any = {};
  agentOptions.forEach(({ value, label }: { value: string; label: string }) => {
    agentsMap[value] = { value, label };
  });

  const initialFilterValues = useMemo(() => {
    const { filters } = controls;

    // Determine status
    let status = null;
    if (filters.includes(FILTER_KEYS.LIVE))
      status = STATUS_OPTIONS[FILTER_KEYS.LIVE];
    else if (filters.includes(FILTER_KEYS.INACTIVE))
      status = STATUS_OPTIONS[FILTER_KEYS.INACTIVE];

    // Determine summary - default to "Has Summary" unless "No Summary" is explicitly set
    let summary = SUMMARY_OPTIONS[FILTER_KEYS.HAS_SUMMARY];
    if (filters.includes(FILTER_KEYS.NO_SUMMARY))
      summary = SUMMARY_OPTIONS[FILTER_KEYS.NO_SUMMARY];

    // Determine outcomes (multiselect)
    const outcomes = filters
      .filter((filter) =>
        [
          FILTER_KEYS.CONVERTED,
          FILTER_KEYS.NOT_CONVERTED,
          FILTER_KEYS.UNSUBSCRIBED,
        ].includes(filter),
      )
      .map(
        (filter) => OUTCOMES_OPTIONS[filter as keyof typeof OUTCOMES_OPTIONS],
      );

    // Determine sentiment (single select)
    let sentiment = null;
    if (filters.includes(FILTER_KEYS.POSITIVE))
      sentiment = SENTIMENT_OPTIONS[FILTER_KEYS.POSITIVE];
    else if (filters.includes(FILTER_KEYS.NEUTRAL))
      sentiment = SENTIMENT_OPTIONS[FILTER_KEYS.NEUTRAL];
    else if (filters.includes(FILTER_KEYS.NEGATIVE))
      sentiment = SENTIMENT_OPTIONS[FILTER_KEYS.NEGATIVE];

    return { status, summary, outcomes, sentiment };
  }, [controls.filters]);

  const closeOnEscape = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && isOpen) {
      toggleFunction(false);
    }
  };

  window.addEventListener('keydown', closeOnEscape);

  return (
    <Formik
      initialValues={{
        shops: shops
          ? shops.filter((shop) => controls.shopIds.includes(+shop.value))
          : [],
        agents: agentOptions
          ? controls.agentIds.map((agentId) => agentsMap[agentId])
          : [],
        searchText: controls.searchText,
        status: initialFilterValues.status,
        summary: initialFilterValues.summary,
        outcomes: initialFilterValues.outcomes,
        sentiment: initialFilterValues.sentiment,
        sortBy: SORT_BY[controls.sortBy] || SORT_BY_KEYS.MOST_RECENT_INBOUND,
        searchDirection:
          SEARCH_DIRECTION[
            controls.searchDirection as keyof typeof SEARCH_DIRECTION
          ] || SEARCH_DIRECTION_KEYS.ALL,
        onlyShopper: controls.onlyShopper ?? true,
      }}
      onSubmit={(values) => {
        const shopId = getShopId();

        const {
          shops,
          agents,
          searchText,
          status,
          summary,
          outcomes,
          sentiment,
          sortBy,
          searchDirection,
          onlyShopper,
        } = values;

        const shopIds = isMultiShop
          ? shops.map((shop: any) => shop.value)
          : [shopId];

        // Build filters array from the new structure
        const filters = [];

        // Add status filter (if selected)
        if (status && status.value) {
          filters.push(status.value);
        }

        // Add summary filter (if selected)
        if (summary && summary.value) {
          filters.push(summary.value);
        }

        // Add outcomes filters (array of selected values)
        if (outcomes && outcomes.length > 0) {
          filters.push(...outcomes.map((outcome: any) => outcome.value));
        }

        // Add sentiment filter (if selected)
        if (sentiment && sentiment.value) {
          filters.push(sentiment.value);
        }

        const controls = {
          searchText,
          shopIds,
          agentIds: agents.map((agent: any) => agent.value),
          filters,
          sortBy: sortBy.value,
          searchDirection: searchDirection.value,
          onlyShopper,
        };
        onControlsUpdate(controls);
        toggleFunction(false);
      }}
    >
      {(formikProps) => (
        <ConversationIntelligenceControlsForm
          {...formikProps}
          controls={controls}
          isLoading={isLoading}
          isMultiShop={isMultiShop}
          isOpen={isOpen}
          toggleFunction={toggleFunction}
          fullClearFunction={fullClearFunction}
          parentRef={parentRef}
          badgeElements={badgeElements}
          shopOptions={shopOptions}
          agentOptions={agentOptions}
          setShopSearchText={setShopSearchText}
          setAgentSearchText={setAgentSearchText}
        />
      )}
    </Formik>
  );
};

export default ConversationIntelligenceControls;
